import { ProductionBatchStatistics } from '@interfaces/search';
import { ncFilesSearch, productionBatchesSearch, productionOrdersSearch, toolsSearch } from '@reducers/search';
import { PaginatedResponse } from '@interfaces/machines';
import { AppThunk } from '@store/types';
import { OPERATOR_PANEL_INFORMATION_SCREENS } from '@components/operatorPanel/constants/operatorPanel.constants';
import { DOC_TYPE } from '@components/imageHandling/common';
import { RetrievedDocumentReference } from '@v2/components/imageHandling/types/types';
import { ImageDocumentStoreItem } from '@v2/components/imageHandling/types/types';
import { TEST_IDS } from '@v2/pages/common/constants/base.constants';

export const TAB_TEST_IDS: Partial<Record<OPERATOR_PANEL_INFORMATION_SCREENS, string>> = {
  [OPERATOR_PANEL_INFORMATION_SCREENS.Jobs]: TEST_IDS.operatorPanel.information.tab.jobs,
  [OPERATOR_PANEL_INFORMATION_SCREENS.NcFiles]: TEST_IDS.operatorPanel.information.tab.ncFiles,
  [OPERATOR_PANEL_INFORMATION_SCREENS.Setup]: TEST_IDS.operatorPanel.information.tab.setup,
  [OPERATOR_PANEL_INFORMATION_SCREENS.Tools]: TEST_IDS.operatorPanel.information.tab.tools,
  [OPERATOR_PANEL_INFORMATION_SCREENS.Notes]: TEST_IDS.operatorPanel.information.tab.notes,
  [OPERATOR_PANEL_INFORMATION_SCREENS.SearchNCFiles]: TEST_IDS.operatorPanel.information.tab.searchNCFiles,
  [OPERATOR_PANEL_INFORMATION_SCREENS.Part]: TEST_IDS.operatorPanel.information.tab.part,
  [OPERATOR_PANEL_INFORMATION_SCREENS['2DDrawing']]: TEST_IDS.operatorPanel.information.tab.twoDDrawing,
  [OPERATOR_PANEL_INFORMATION_SCREENS['3DDrawing']]: TEST_IDS.operatorPanel.information.tab.threeDDrawing,
  [OPERATOR_PANEL_INFORMATION_SCREENS.Clamping]: TEST_IDS.operatorPanel.information.tab.clamping,
  [OPERATOR_PANEL_INFORMATION_SCREENS.ProductionOrder]: TEST_IDS.operatorPanel.information.tab.productionOrder,
  [OPERATOR_PANEL_INFORMATION_SCREENS.Measurement]: TEST_IDS.operatorPanel.information.tab.measurement,
  [OPERATOR_PANEL_INFORMATION_SCREENS.AllDocuments]: TEST_IDS.operatorPanel.information.tab.allDocuments,
  [OPERATOR_PANEL_INFORMATION_SCREENS.SearchHistory]: TEST_IDS.operatorPanel.information.tab.searchHistory,
};

export enum OPTabsPerspective {
  MACHINE = 'Machine',
  JOB = 'Job',
}

export interface TabUpdater {
  api?: (params: {
    jobIds?: number[];
    ncFileIds?: number[];
    productionOrderIds?: number[];
    ncFileIdsForTools?: number[];
  }) => Promise<PaginatedResponse<RetrievedDocumentReference>> | AppThunk<Promise<PaginatedResponse<RetrievedDocumentReference>>>;
  getDataFromImages?: (images: ImageDocumentStoreItem[]) => ImageDocumentStoreItem[];
}

export type NullableBatch = ProductionBatchStatistics | null;

const emptyResult = { searchResult: [], totalResultCount: 0 };

export const tabUpdaters: Partial<Record<OPERATOR_PANEL_INFORMATION_SCREENS, TabUpdater>> = {
  [OPERATOR_PANEL_INFORMATION_SCREENS.Jobs]: {
    api:
      ({ jobIds }) =>
      () =>
        Promise.resolve({ searchResult: [], totalResultCount: jobIds?.length ?? 0 }),
  },
  [OPERATOR_PANEL_INFORMATION_SCREENS.NcFiles]: {
    api:
      ({ ncFileIds }) =>
      () =>
        Promise.resolve({ searchResult: [], totalResultCount: ncFileIds?.length ?? 0 }),
  },
  [OPERATOR_PANEL_INFORMATION_SCREENS.Setup]: {
    getDataFromImages: (images) => images.filter((i) => i.documentTag === DOC_TYPE.SETUP_SHEET),
  },
  [OPERATOR_PANEL_INFORMATION_SCREENS.Tools]: {
    api: ({ ncFileIdsForTools }) =>
      !ncFileIdsForTools?.length
        ? () => Promise.resolve(emptyResult)
        : toolsSearch({
            data: {
              top: 0,
              skip: 0,
            },
            filter: {
              oDataFilter: `ClampingTools/any (tool: (${ncFileIdsForTools.map((id) => `tool/NcFileId eq ${id}`).join(' or ')}))`,
            },
            select: [],
            resetHistory: true,
            ignoreHistory: true,
          }),
  },
  [OPERATOR_PANEL_INFORMATION_SCREENS.Notes]: {
    getDataFromImages: (images) => images.filter((i) => i.documentTag === DOC_TYPE.NOTE),
  },
  [OPERATOR_PANEL_INFORMATION_SCREENS['2DDrawing']]: {
    getDataFromImages: (images) => images.filter((i) => i.documentTag === DOC_TYPE['2D_DRAWING']),
  },
  [OPERATOR_PANEL_INFORMATION_SCREENS['3DDrawing']]: {
    getDataFromImages: (images) => images.filter((i) => i.documentTag === DOC_TYPE['3D_DRAWING']),
  },
  [OPERATOR_PANEL_INFORMATION_SCREENS.Clamping]: {
    getDataFromImages: (images) => images.filter((i) => i.documentTag === DOC_TYPE.CLAMPING_IMAGE),
  },
  [OPERATOR_PANEL_INFORMATION_SCREENS.Part]: {
    getDataFromImages: (images) => images.filter((i) => i.documentTag === DOC_TYPE.PART_IMAGE),
  },
  // [OPERATOR_PANEL_INFORMATION_SCREENS.Material]: {
  //   // Material implementation would go here
  // },
  [OPERATOR_PANEL_INFORMATION_SCREENS.ProductionOrder]: {
    api: ({ productionOrderIds }) =>
      !productionOrderIds?.length
        ? () => Promise.resolve(emptyResult)
        : productionOrdersSearch({
            data: {
              top: 0,
              skip: 0,
            },
            filter: {
              oDataFilter: `${productionOrderIds.map((id) => `ProductionOrderId eq ${id}`).join(' or ')}`,
            },
            select: null,
            resetHistory: true,
            ignoreHistory: true,
          }),
  },
  [OPERATOR_PANEL_INFORMATION_SCREENS.Measurement]: {
    getDataFromImages: (images) => images.filter((i) => i.documentTag === DOC_TYPE.MEASUREMENT_REPORT),
  },
  [OPERATOR_PANEL_INFORMATION_SCREENS.AllDocuments]: {
    getDataFromImages: (images) => images,
  },
  [OPERATOR_PANEL_INFORMATION_SCREENS.Scheduled]: {},
  [OPERATOR_PANEL_INFORMATION_SCREENS.SearchNCFiles]: {
    api: () =>
      ncFilesSearch({
        data: { top: 0, skip: 0 },
        filter: { oDataFilter: null },
        select: [],
        resetHistory: true,
      }),
  },
  [OPERATOR_PANEL_INFORMATION_SCREENS.SearchHistory]: {
    api: () =>
      productionBatchesSearch({
        data: { top: 0, skip: 0 },
        filter: {
          orderBy: ['EndedOn desc'],
        },
        select: [],
        resetHistory: true,
        ignoreHistory: true,
        signal: undefined,
      }),
  },
};
